/**
 * Form validation utilities (JS/JSX version)
 */

// import { OrganizationFormData } from "../types/organization";

/**
 * @typedef {Object} ValidationErrors
 * @property {string=} organizationName
 * @property {string=} industry
 * @property {string=} street
 * @property {string=} city
 * @property {string=} state
 * @property {string=} zipCode
 */

/**
 * Validates organization form data
 * @param {Object} data OrganizationFormData-like object
 * @returns {ValidationErrors}
 */
export function validateOrganizationForm(data) {
  /** @type {ValidationErrors} */
  const errors = {};

  if (!String(data?.organizationName || '').trim()) {
    errors.organizationName = 'Organization name is required';
  }

  if (!data?.industry) {
    errors.industry = 'Please select an industry';
  }

  if (!String(data?.street || '').trim()) {
    errors.street = 'Street address is required';
  }

  if (!String(data?.city || '').trim()) {
    errors.city = 'City is required';
  }

  if (!String(data?.state || '').trim()) {
    errors.state = 'State is required';
  }

  const zip = String(data?.zipCode || '').trim();

  if (!zip) {
    errors.zipCode = 'Zip code is required';
  } else if (!/^\d{5}(-\d{4})?$/.test(zip)) {
    errors.zipCode = 'Invalid zip code format';
  }

  return errors;
}

/**
 * Checks if form has any validation errors
 * @param {ValidationErrors} errors
 * @returns {boolean}
 */
export function hasValidationErrors(errors) {
  return Object.keys(errors || {}).length > 0;
}

/**
 * Parse time string "HH:mm" to minutes since midnight
 * @param {string} time
 * @returns {number}
 */
function parseTimeToMinutes(time) {
  if (!time || typeof time !== 'string') return 0;
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * Calculate minutes between two time strings "HH:mm"
 * @param {string} from
 * @param {string} to
 * @returns {number}
 */
export function calculateMinutes(from, to) {
  const fromMin = parseTimeToMinutes(from);
  const toMin = parseTimeToMinutes(to);
  if (toMin <= fromMin) return 0;
  return toMin - fromMin;
}

/**
 * Validate cleaning processes fit within window and optional other tasks
 * @param {Array<{ timeStandard: { minimum: number, maximum: number } }>} processes
 * @param {number} totalMinutes
 * @param {number} [otherTasksMin]
 * @param {number} [otherTasksMax]
 * @returns {{ status: 'feasible'|'tight'|'impossible', message: string, details?: Object }}
 */
export function validateProcessConfiguration(processes, totalMinutes, otherTasksMin, otherTasksMax) {
  const devicesMin = (processes || []).reduce((sum, p) => sum + (p.timeStandard?.minimum ?? 0), 0);
  const devicesMax = (processes || []).reduce((sum, p) => sum + (p.timeStandard?.maximum ?? 0), 0);
  const otherMin = otherTasksMin ?? 0;
  const otherMax = otherTasksMax ?? 0;
  const requiredMax = devicesMax + otherMax;
  const margin = totalMinutes - requiredMax;

  const details = {
    available: totalMinutes,
    devicesMin,
    devicesMax,
    otherTasksMin: otherMin,
    otherTasksMax: otherMax,
    margin,
  };

  if (margin < 0) {
    return {
      status: 'impossible',
      message: `Total time required (${requiredMax} min) exceeds the cleaning window (${totalMinutes} min).`,
      details,
    };
  }
  if (margin <= 15) {
    return {
      status: 'tight',
      message: `Schedule is tight: only ${margin} min buffer. Consider reducing device times or other tasks.`,
      details,
    };
  }
  return {
    status: 'feasible',
    message: `Configuration fits within the window with ${margin} min buffer.`,
    details,
  };
}
